// Pure data helpers for the chart RPM tab. No React / Chart.js imports so they
// can be checked with plain node.
//
// The tab plots every reading on trendslinegraph's category axis. That chart
// uses the x label as the reading's identity: it re-joins diastolic to systolic
// by label and merges weight/BMI by label. The API's `datelabel`
// ("MM-DD-YYYY hh:mm AM") is only minute-precise, so two readings in the same
// minute shared a label and the chart showed the wrong diastolic (or dropped a
// weight). Each reading therefore gets its own label, and the axis text is
// derived back from it (rpmAxisLabel) so it still reads like `datelabel`.
import { parseRpmTimestamp } from "../../../../graphs/linegraphs/rpmsittings";

const pad2 = (n) => String(n).padStart(2, "0");

// Date -> "MM-DD-YYYY HH:mm:ss". trendslinegraph's parseLabelDate reads this
// shape as local time in Chrome, Safari and Firefox (Safari cannot parse the
// AM/PM `datelabel`, so PM readings used to sort as AM there).
const toLabel = (d) =>
  `${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}-${d.getFullYear()} ` +
  `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;

// One chart label per reading, in the API's order. stamps[i] (bpp-master
// `datetime`, to the second) is used when it parses, else datelabels[i].
// A label that repeats gets " #2", " #3", ...; parseLabelDate ignores anything
// after the time, so that point still sorts by its real time.
export function buildRpmChartLabels(stamps, datelabels) {
  const st = Array.isArray(stamps) ? stamps : [];
  const dl = Array.isArray(datelabels) ? datelabels : [];
  const seen = new Map();
  return dl.map((datelabel, i) => {
    const d = parseRpmTimestamp(st[i]) || parseRpmTimestamp(datelabel);
    const base = d ? toLabel(d) : String(datelabel ?? "");
    const n = (seen.get(base) || 0) + 1;
    seen.set(base, n);
    return n === 1 ? base : `${base} #${n}`;
  });
}

// X-axis text for a chart label: "MM-DD-YYYY hh:mm AM", the same text as the
// API's `datelabel` (what the axis showed when it plotted `datelabel` itself).
export function rpmAxisLabel(label) {
  const s = String(label ?? "");
  const m = /^(\d{2}-\d{2}-\d{4}) (\d{2}):(\d{2})/.exec(s);
  if (!m) return s;
  const hour = Number(m[2]);
  const ampm = hour >= 12 ? "PM" : "AM";
  return `${m[1]} ${pad2(hour % 12 || 12)}:${m[3]} ${ampm}`;
}

// "All Readings History" rows for a sub-tab (0 BP, 1 Pulse, 2 Weight), newest
// first. rows: { date, val1, val2, unit, time }.
export function buildRpmHistoryRows(tab, rpmData, wlmData) {
  const bpp = rpmData || {};
  const wlm = wlmData || {};
  const rows = [];

  if (tab === 0 || tab === 1) {
    const labels = bpp.datelabel || [];
    const stamps = bpp.datetime || [];
    const sys = bpp.systolic || [];
    const dia = bpp.diastolic || [];
    const pulse = bpp.pulse || [];
    labels.forEach((date, i) => {
      const time = parseRpmTimestamp(stamps[i]) || parseRpmTimestamp(date);
      rows.push(
        tab === 0
          ? { date, val1: sys[i], val2: dia[i], time }
          : { date, val1: pulse[i], time }
      );
    });
  } else if (tab === 2) {
    // wlm-master's `datetime` list is the row's insert time, not the reading's.
    const labels = wlm.datelabel || [];
    const weight = wlm.weight || [];
    const bmi = wlm.bmi || [];
    const units = wlm.weightunits || [];
    labels.forEach((date, i) => {
      rows.push({
        date,
        val1: weight[i],
        val2: bmi[i],
        unit: units[i] || "lbs",
        time: parseRpmTimestamp(date),
      });
    });
  }

  const ms = (r) => (r.time ? r.time.getTime() : 0);
  rows.sort((a, b) => ms(b) - ms(a));
  return rows;
}
